import type { FilterItem, KeyedArray } from "@repo/models";

import { useMemo } from "react";

import type { MarketFilterItem } from "../models";

import { idType } from ".";
import { GridFiltersController } from "../api";
import { useFilterItemQuery } from "../api";
import * as Queries from "../api/queries";

export function useCompanySegmentFilterCascaded<T = KeyedArray<FilterItem>>(
	segmentProp: ICompanySegment,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	const segment = segmentProp || ({} as ICompanySegment);
	const [companies, companiesLoaded] = useFilterItemQuery([Queries.COMPANIES], GridFiltersController.getCompanies);
	const [divisions, divisionsLoaded] = useFilterItemQuery([Queries.DIVISIONS], GridFiltersController.getDivisions);
	const [regions, regionsLoaded] = useFilterItemQuery([Queries.REGIONS], GridFiltersController.getRegions);
	const [markets, marketsLoaded] = useFilterItemQuery([Queries.MARKETS], GridFiltersController.getMarkets);
	const [managementAreas, managementAreasLoaded] = useFilterItemQuery(
		[Queries.QUERY_KEYS.Companies.ManagementAreas],
		GridFiltersController.getManagementAreas,
	);

	/** Markets */
	const filteredMarketsOnCompany = useMemo(
		() =>
			getMarketsFromFilter({ companyId: segment.companyId, companyIds: segment.companyIds }, markets?.data ?? []),
		[segment, markets],
	);
	const filteredMarketsOnDivision = useMemo(
		() =>
			getMarketsFromFilter(
				{ companyDivisionId: segment.companyDivisionId, companyDivisionIds: segment.companyDivisionIds },
				filteredMarketsOnCompany,
			),
		[segment, markets, filteredMarketsOnCompany],
	);
	const filteredMarketsOnRegion = useMemo(
		() =>
			getMarketsFromFilter(
				{ companyRegionId: segment.companyRegionId, companyRegionIds: segment.companyRegionIds },
				filteredMarketsOnDivision,
			),
		[segment, markets, filteredMarketsOnDivision],
	);

	const filteredMarkets = useMemo(
		() => getMarketsFromFilter(segment, filteredMarketsOnRegion),
		[segment, companies, divisions, regions, markets],
	);

	/**Management Areas*/
	const filteredManagementAreasOnCompany = useMemo(
		() =>
			getMarketsFromFilter(
				{ companyId: segment.companyId, companyIds: segment.companyIds },
				managementAreas?.data ?? [],
			),
		[segment, managementAreas],
	);
	const filteredManagementAreasOnDivision = useMemo(
		() =>
			getMarketsFromFilter(
				{ companyDivisionId: segment.companyDivisionId, companyDivisionIds: segment.companyDivisionIds },
				filteredManagementAreasOnCompany,
			),
		[segment, filteredManagementAreasOnCompany],
	);
	const filteredManagementAreasOnRegion = useMemo(
		() =>
			getMarketsFromFilter(
				{ companyRegionId: segment.companyRegionId, companyRegionIds: segment.companyRegionIds },
				filteredManagementAreasOnDivision,
			),
		[segment, filteredManagementAreasOnDivision],
	);

	const filteredManagementAreas = useMemo(
		() => getMarketsFromFilter(segment, filteredManagementAreasOnRegion),
		[segment, filteredManagementAreasOnRegion],
	);

	const activeSegmentLists = useMemo(() => {
		return {
			companies: companies,
			divisions: getRelevantIdsFromMarkets(filteredMarketsOnCompany, divisions, (b) => b.divisionId),
			regions: getRelevantIdsFromMarkets(filteredMarketsOnDivision, regions, (b) => b.regionId),
			markets: getRelevantIdsFromMarkets(filteredMarketsOnRegion, markets, (b) => b.id),
			managementAreas: getRelevantIdsFromMarkets(filteredManagementAreasOnRegion, managementAreas, (b) => b.id),
			companiesLoaded,
			divisionsLoaded,
			regionsLoaded,
			marketsLoaded,
			managementAreasLoaded,
		} as ICompanySegmentList<T>;
	}, [filteredMarkets, filteredManagementAreas, companies, divisions, regions]);

	return activeSegmentLists;

	function getMarketsFromFilter(segmentPart: Partial<ICompanySegment>, filteredMarkets: MarketFilterItem[]) {
		if (!filteredMarkets.length) return filteredMarkets;
		//if (!segment.showInactiveOffices) filteredMarkets = filteredMarkets.filter((m) => m.Active ?? true);
		filteredMarkets = filteredMarkets
			.filter((m) => !segmentPart.companyId || m.companyId === segmentPart.companyId)
			.filter((m) => !segmentPart.companyDivisionId || m.divisionId === segmentPart.companyDivisionId)
			.filter((m) => !segmentPart.companyRegionId || m.regionId === segmentPart.companyRegionId);
		filteredMarkets = filteredMarkets
			.filter((m) => !segmentPart.companyIds?.length || segmentPart.companyIds.includes(m.companyId))
			.filter(
				(m) => !segmentPart.companyDivisionIds?.length || segmentPart.companyDivisionIds.includes(m.divisionId),
			)
			.filter((m) => !segmentPart.companyRegionIds?.length || segmentPart.companyRegionIds.includes(m.regionId));
		return filteredMarkets;
	}

	function getRelevantIdsFromMarkets(
		markets: MarketFilterItem[],
		allItems: KeyedArray<FilterItem>,
		selector: (b: MarketFilterItem) => number,
	) {
		if (!markets.length) return mapItems ? mapItems(allItems) : allItems;
		const groups = markets.groupBy(selector);
		const relevantIds = allItems.filter((i) => groups.has(i.id));
		if (mapItems) return mapItems(relevantIds);
		return relevantIds;
	}
}

export interface ICompanySegment {
	companyId?: idType;
	companyDivisionId?: idType;
	companyRegionId?: idType;
	companyMarketId?: idType;
	companyManagementAreaId?: idType;

	companyIds?: idType[];
	companyDivisionIds?: idType[];
	companyRegionIds?: idType[];
	companyMarketIds?: idType[];
	companyManagementAreaIds?: idType[];
}

export interface ICompanySegmentList<T = KeyedArray<FilterItem>> {
	companies: T;
	divisions: T;
	regions: T;
	markets: T;
	managementAreas: T;
	companiesLoaded: boolean;
	divisionsLoaded: boolean;
	regionsLoaded: boolean;
	marketsLoaded: boolean;
	managementAreasLoaded: boolean;
}
